import { DevelopmentWorksheetDetails } from "@/model/employee-work-details/development-worksheet-details";
import { Component, OnInit } from "@angular/core";
import { AppService } from "@services/app.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-development-worksheet-entry-table",
  templateUrl: "./development-worksheet-entry-table.component.html",
  styleUrl: "./development-worksheet-entry-table.component.scss",
})
export class DevelopmentWorksheetEntryTableComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  wagsMonth: string = "";
  getAllWagsMonth: string[] = [];
  worksheetEntryDetails: DevelopmentWorksheetDetails[] = [];
  aggregatedData: AggregatedDevelopmentEmployeeData[] = [];
  allAggregatedData: AggregatedDevelopmentEmployeeData[] = [];
  employeeNames: string[] = [];
  selectedEmployee: AggregatedDevelopmentEmployeeData | null = null;
  selectedEmployeeName: string = "";
  isShowPopup: boolean = false;
  paymentUpdatingId: number | null = null;

  constructor(
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toaster: ToastrService
  ) {}

  ngOnInit(): void {
    this.getWagsMonthList();
  }

  /* Get wags month List */
  getWagsMonthList() {
    this.spinner.show();
    this.appService
      .getListOfWagsMonthForDevelopment()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          // Filter out empty strings
          this.getAllWagsMonth = data.filter((item) => item != "");

          this.getAllWagsMonth.sort((a, b) => {
            const dateA = new Date("01-" + a);
            const dateB = new Date("01-" + b);
            return dateB.getTime() - dateA.getTime();
          });

          // Set the first (latest) month
          this.wagsMonth = this.getAllWagsMonth[0];
          this.wagsMonthBasedDetailsGet(this.wagsMonth);
        },
        (error: any) => {
          // Handle error (optional)
          this.spinner.hide();
        }
      );
  }

  onWagsMonthChange(): void {

    // Get currently selected month
    const selectedMonth = this.wagsMonth;

    console.log('Selected WAGS Month:', selectedMonth);

    if (!selectedMonth) {
        return;
    }

    // Reset employee filter
    this.selectedEmployeeName = '';

    // Get details for selected month
    this.wagsMonthBasedDetailsGet(selectedMonth);
}

  /* get wags month based worksheet details  */
  wagsMonthBasedDetailsGet(month: string) {
    this.spinner.show();
    this.appService
      .wagsMonthBasedDetailsGetForDevelopment(month)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.spinner.hide();
          this.worksheetEntryDetails = data.sort((a,b) => b.devEmployeeTrackId - a.devEmployeeTrackId);;
          this.processData();
        },
        (error: any) => {
          this.toaster.error(
            error.error.message || "Something went wrong,please try again....."
          );
          this.spinner.hide();
        }
      );
  }

processData() {
  const map = new Map<string, AggregatedDevelopmentEmployeeData>();

  this.worksheetEntryDetails.forEach((entry: any) => {
    const employeeName = entry.employeeName?.trim() || 'Unknown';
    const taskType = (entry.taskType || '').toLowerCase();

    const startTime = Number(entry.startTime);
    const endTime = Number(entry.endTime);

    const durationInMinutes = this.getTimeInMinutes(startTime, endTime);

    if (!map.has(employeeName)) {
      map.set(employeeName, {
        employeeName,
        totalMinutes: 0,
        uiMinutes: 0,
        functionalityMinutes: 0,
        apiMinutes: 0,
        deploymentMinutes: 0,
        mkuMinutes: 0,
        testingMinutes: 0,
        othersMinutes: 0,
        databaseMinutes: 0,
        entries: [],
      });
    }

    const group = map.get(employeeName)!;

    group.totalMinutes += durationInMinutes;

    switch (taskType) {
      case 'ui':
        group.uiMinutes += durationInMinutes;
        break;
      case 'functionality':
        group.functionalityMinutes += durationInMinutes;
        break;
      case 'api':
        group.apiMinutes += durationInMinutes;
        break;
      case 'deployment':
        group.deploymentMinutes += durationInMinutes;
        break;
      case 'mku':
        group.mkuMinutes += durationInMinutes;
        break;
        case 'testing':
        group.testingMinutes += durationInMinutes;
        break;
      case 'others':
        group.othersMinutes += durationInMinutes;
        break;
      case 'database':
        group.databaseMinutes += durationInMinutes;
        break;
    }

    group.entries.push(entry);
  });

  // Convert all minutes to formatted HH.MM time
  this.allAggregatedData = Array.from(map.values()).map((group) => ({
    employeeName: group.employeeName,
    totalWorkHours: this.convertMinutesToHHMM(group.totalMinutes),
    uiWorkHours: this.convertMinutesToHHMM(group.uiMinutes),
    functionalityHours: this.convertMinutesToHHMM(group.functionalityMinutes),
    apiHours: this.convertMinutesToHHMM(group.apiMinutes),
    deploymentHours: this.convertMinutesToHHMM(group.deploymentMinutes),
    mkuHours: this.convertMinutesToHHMM(group.mkuMinutes),
    testingHoures: this.convertMinutesToHHMM(group?.testingMinutes),
    othersHours: this.convertMinutesToHHMM(group.othersMinutes),
    databaseHours: this.convertMinutesToHHMM(group.databaseMinutes),
    entries: group.entries,
  }));

  this.aggregatedData = [...this.allAggregatedData];
  this.employeeNames = this.allAggregatedData.map((d) => d.employeeName);
}

getPaymentStatus(entry: any): string {

    if (!entry) {
        return 'UNPAID';
    }

    return (entry.paymentStatus || 'UNPAID')
        .toString()
        .trim()
        .toUpperCase();
}

updatePaymentStatus(entry: any): void {

    if (!entry || !entry.devEmployeeTrackId) {

        this.toaster.error(
            'Worksheet tracking ID not found.'
        );

        return;
    }

    const devEmployeeTrackId = Number(entry.devEmployeeTrackId);

    // Current status
    const currentStatus = this.getPaymentStatus(entry);

    // Toggle status
    const newPaymentStatus =
        currentStatus === 'PAID'
            ? 'UNPAID'
            : 'PAID';

    // Prevent duplicate API calls
    if (this.paymentUpdatingId === devEmployeeTrackId) {
        return;
    }

    const jsonData = {
        devEmployeeTrackId: [devEmployeeTrackId],
        paymentStatus: newPaymentStatus
    };

    console.log(
        'Payment Status Update Request:',
        jsonData
    );

    this.paymentUpdatingId = devEmployeeTrackId;

    this.spinner.show();

    this.appService
        .updateDevelopmentWorksheetPaymentStatus(jsonData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({

            next: (response: any) => {

                console.log(
                    'Payment Status Update Response:',
                    response
                );

                // Update popup row immediately
                entry.paymentStatus = newPaymentStatus;

                // Also update original worksheet data
                const worksheetEntry =
                    this.worksheetEntryDetails.find(
                        (item: any) =>
                            Number(item.devEmployeeTrackId) ===
                            devEmployeeTrackId
                    );

                if (worksheetEntry) {
                    (worksheetEntry as any).paymentStatus =
                        newPaymentStatus;
                }

                // Update selected employee entry
                if (this.selectedEmployee?.entries) {

                    const selectedEntry =
                        this.selectedEmployee.entries.find(
                            (item: any) =>
                                Number(item.devEmployeeTrackId) ===
                                devEmployeeTrackId
                        );

                    if (selectedEntry) {
                        (selectedEntry as any).paymentStatus =
                            newPaymentStatus;
                    }
                }

                this.toaster.success(
                    `Payment status updated to ${newPaymentStatus === 'PAID' ? 'Paid' : 'Unpaid'}.`
                );

                this.paymentUpdatingId = null;

                this.spinner.hide();
            },

            error: (error: any) => {

                console.error(
                    'Payment Status Update Error:',
                    error
                );

                this.toaster.error(
                    error?.error?.message ||
                    'Unable to update payment status.'
                );

                this.paymentUpdatingId = null;

                this.spinner.hide();
            }

        });
}

getTimeInMinutes(startTime: number, endTime: number): number {
  if (!startTime || !endTime || endTime <= startTime) return 0;
  return Math.floor((endTime - startTime) / (1000 * 60)); // minutes
}

convertMinutesToHHMM(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}.${minutes.toString().padStart(2, '0')}`;
}



  openModal(employee: AggregatedDevelopmentEmployeeData) {
    this.selectedEmployee = employee;
    this.isShowPopup = true;
  }

  closeModal() {
    this.selectedEmployee = null;
    this.isShowPopup = false;
  }

  filterByEmployee() {
    if (this.selectedEmployeeName) {
      this.aggregatedData = this.allAggregatedData.filter(
        (d) => d.employeeName === this.selectedEmployeeName
      );
    } else {
      this.aggregatedData = [...this.allAggregatedData];
    }
  }
}

export interface AggregatedDevelopmentEmployeeData {
   employeeName: string;
  totalMinutes?: number;
  uiMinutes?: number;
  functionalityMinutes?: number;
  apiMinutes?: number;
  deploymentMinutes?: number;
  mkuMinutes?: number;
  testingMinutes?: number;
  othersMinutes?: number;
  databaseMinutes?: number;
  // Final display values
  totalWorkHours?: string;
  uiWorkHours?: string;
  functionalityHours?: string;
  apiHours?: string;
  deploymentHours?: string;
  mkuHours?: string;
  othersHours?: string;
  testingHoures?: string;
  databaseHours?: string;
  entries: DevelopmentWorksheetDetails[];
}
